/**
 * What a correction actually changed. The backend keeps a revision entry per
 * PATCH (`edit_log`), so this renders each one in plain words — old value on
 * the left, new value on the right.
 */
import React, { useState } from 'react';
import { History, ChevronDown, ChevronRight, ArrowRight, RotateCcw } from 'lucide-react';
import { LevelChip } from './shared';

const stamp = (iso) => (iso || '').slice(0, 16).replace('T', ' ');

const Value = ({ text }) => (
  <span className="text-slate-700">{text ? `“${text}”` : <i className="text-slate-400">empty</i>}</span>
);

const ChangeRow = ({ change, skillName }) => {
  if (change.field === 'rating') {
    return (
      <li className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500">{skillName(change.skill_id)}</span>
        <LevelChip level={change.from} size="sm" />
        <ArrowRight size={11} className="text-slate-400" />
        <LevelChip level={change.to} size="sm" />
      </li>
    );
  }
  if (change.field === 'skill_index') {
    return (
      <li className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500">Skill index</span>
        <span className="font-mono text-slate-700">
          {change.from === null || change.from === undefined ? '—' : `${change.from}%`}
        </span>
        <ArrowRight size={11} className="text-slate-400" />
        <span className="font-mono font-semibold text-slate-900">
          {change.to === null || change.to === undefined ? '—' : `${change.to}%`}
        </span>
      </li>
    );
  }
  return (
    <li className="flex flex-wrap items-start gap-2">
      <span className="text-slate-500 shrink-0">{change.label}</span>
      <Value text={change.from} />
      <ArrowRight size={11} className="mt-1 text-slate-400 shrink-0" />
      <Value text={change.to} />
    </li>
  );
};

export const AssessmentEditLog = ({ log = [], catalogue = [], onRestore }) => {
  const [open, setOpen] = useState(false);
  if (!log.length) return null;

  const skillName = (id) => (catalogue.find((s) => s.id === id) || {}).name || 'Skill';

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-slate-600 hover:text-slate-900"
        data-testid="edit-log-toggle"
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <History size={13} />
        Edit history · {log.length} {log.length === 1 ? 'correction' : 'corrections'}
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-2.5" data-testid="edit-log-body">
          {log.map((rev, i) => ({ rev, i })).reverse().map(({ rev, i }) => (
            <div key={`${rev.at}-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  {stamp(rev.at)} · {rev.by || 'unknown'}
                  {rev.restored_to && <> · rolled back to before {stamp(rev.restored_to)}</>}
                </div>
                {onRestore && (
                  <button
                    type="button"
                    onClick={() => onRestore(i, rev)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-600 hover:border-teal-400 hover:text-teal-700"
                    title="Put the values back to how they were before this correction"
                    data-testid={`restore-revision-${i}`}
                  >
                    <RotateCcw size={10} /> Undo this
                  </button>
                )}
              </div>
              <ul className="mt-1 space-y-1 text-xs">
                {(rev.changes || []).map((c, j) => (
                  <ChangeRow key={j} change={c} skillName={skillName} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentEditLog;
